<template>
  <div class="download-page">
    <header class="page-header">
      <div class="container">
        <div class="header-row">
          <div class="header-left">
            <h2 class="current-category-title" v-if="!searchKeyword">下载专区</h2>
            <h2 class="current-category-title" v-else>搜索结果 - "{{ searchKeyword }}"</h2>
            <nav class="breadcrumb" v-if="!searchKeyword">
              <a href="/">首页</a>
              <span>> 下载专区</span>
            </nav>
          </div>
          <div class="search-box">
            <input type="text" v-model="searchKeyword" :placeholder="searchKeyword ? '搜索全站文件' : '搜索下载文件'" @keyup.enter="doSearch" />
            <button @click="doSearch">搜索</button>
          </div>
        </div>
      </div>
    </header>

    <div class="container">
      <div class="main-content">
        <div class="download-list">
          <div v-for="item in downloads" :key="item.id" class="download-item" @click="handleDownload(item)">
            <div class="download-icon">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <rect width="48" height="48" rx="8" fill="#4ecdc4" opacity="0.1"/>
                <path d="M24 12V28M24 28L18 22M24 28L30 22M16 34H32" stroke="#4ecdc4" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="download-content">
              <h3 class="download-title">{{ item.title }}</h3>
              <div class="download-meta">
                <span class="meta-item" v-if="item.type">类型：{{ item.type }}</span>
                <span class="meta-item">发布日期：{{ formatDate(item.updt || item.created_at) }}</span>
              </div>
            </div>
            <div class="download-action">
              <button class="download-btn">下载</button>
            </div>
          </div>
        </div>

        <div class="pagination-wrapper">
          <span class="total-text">共 {{ totalItems }} 条</span>
          <button class="page-btn" :disabled="currentPage === 1" @click="prevPage">&lt;</button>
          
          <template v-if="totalPages <= 7">
            <button v-for="page in visiblePages" :key="page" class="page-btn" :class="{ active: currentPage === page }" @click="goToPage(page)">
              {{ page }}
            </button>
          </template>
          
          <template v-else>
            <template v-if="currentPage <= 4">
              <button v-for="page in visiblePages" :key="page" class="page-btn" :class="{ active: currentPage === page }" @click="goToPage(page)">
                {{ page }}
              </button>
              <span class="ellipsis">...</span>
              <button class="page-btn" @click="goToPage(totalPages)">{{ totalPages }}</button>
            </template>
            
            <template v-else-if="currentPage >= totalPages - 3">
              <button class="page-btn" @click="goToPage(1)">1</button>
              <span class="ellipsis">...</span>
              <button v-for="page in visiblePages" :key="page" class="page-btn" :class="{ active: currentPage === page }" @click="goToPage(page)">
                {{ page }}
              </button>
            </template>
            
            <template v-else>
              <button class="page-btn" @click="goToPage(1)">1</button>
              <span class="ellipsis">...</span>
              <button v-for="page in visiblePages.slice(2, 5)" :key="page" class="page-btn" :class="{ active: currentPage === page }" @click="goToPage(page)">
                {{ page }}
              </button>
              <span class="ellipsis">...</span>
              <button class="page-btn" @click="goToPage(totalPages)">{{ totalPages }}</button>
            </template>
          </template>
          
          <button class="page-btn" :disabled="currentPage >= totalPages" @click="nextPage">&gt;</button>
          <select class="page-size-select" v-model="pageSize" @change="onPageSizeChange">
            <option value="10">10 条/页</option>
            <option value="20">20 条/页</option>
            <option value="30">30 条/页</option>
          </select>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'Download',
  data() {
    return {
      downloads: [],
      currentPage: 1,
      pageSize: 10,
      totalPages: 1,
      totalItems: 0,
      searchKeyword: ''
    }
  },
  computed: {
    visiblePages() {
      const pages = []
      const total = this.totalPages
      const current = this.currentPage
      
      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i)
        }
      } else {
        if (current <= 4) {
          for (let i = 1; i <= 5; i++) {
            pages.push(i)
          }
        } else if (current >= total - 3) {
          for (let i = total - 4; i <= total; i++) {
            pages.push(i)
          }
        } else {
          pages.push(1)
          pages.push(current - 1, current, current + 1)
          pages.push(total)
        }
      }
      return pages
    }
  },
  watch: {
    '$route.query': {
      handler(newQuery, oldQuery) {
        if (newQuery.keyword !== undefined) {
          this.searchKeyword = newQuery.keyword
        } else if (!oldQuery || (!oldQuery.keyword && newQuery.keyword === undefined)) {
          this.searchKeyword = ''
        }
        if (newQuery.page) {
          this.currentPage = parseInt(newQuery.page) || 1
        } else if (!oldQuery || (!oldQuery.page && newQuery.page === undefined)) {
          this.currentPage = 1
        }
        this.fetchDownloads()
      },
      immediate: false
    }
  },
  mounted() {
    const queryKeyword = this.$route.query.keyword
    const queryPage = this.$route.query.page
    if (queryKeyword) {
      this.searchKeyword = queryKeyword
    }
    if (queryPage) {
      this.currentPage = parseInt(queryPage) || 1
    }
    window.scrollTo(0, 0)
    this.fetchDownloads()
  },
  methods: {
    async fetchDownloads() {
      try {
        const params = {
          page: this.currentPage,
          size: this.pageSize
        }
        if (this.searchKeyword) {
          params.keyword = this.searchKeyword
        }
        const response = await axios.get('/api/downloads', { params })
        console.log('下载专区API响应:', response.data)
        this.downloads = response.data.data || []
        const total = response.data.total || this.downloads.length
        this.totalItems = total
        this.totalPages = Math.ceil(total / this.pageSize)
      } catch (error) {
        console.error('获取下载文件失败:', error)
        this.downloads = []
      }
    },
    doSearch() {
      this.currentPage = 1
      if (this.searchKeyword.trim()) {
        this.$router.push({ path: '/downloads', query: { keyword: this.searchKeyword.trim() } })
      } else {
        this.$router.push({ path: '/downloads' })
      }
    },
    handleDownload(item) {
      console.log('点击下载文件:', item)
      const downloadUrl = item.download_url || ''
      if (!downloadUrl) {
        alert('该文件暂无下载地址')
        return
      }
      window.open(downloadUrl, '_blank')
    },
    updateUrlWithPage() {
      const newQuery = { ...this.$route.query }
      if (this.currentPage > 1) {
        newQuery.page = this.currentPage
      } else {
        delete newQuery.page
      }
      this.$router.replace({ path: '/downloads', query: newQuery })
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--
        this.updateUrlWithPage()
        this.fetchDownloads()
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
        this.updateUrlWithPage()
        this.fetchDownloads()
      }
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
        this.currentPage = page
        this.updateUrlWithPage()
        this.fetchDownloads()
      }
    },
    onPageSizeChange() {
      this.currentPage = 1
      this.fetchDownloads()
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const str = String(dateStr)
      // 如果已经是 yyyy-MM-dd 格式，直接返回
      if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
        return str
      }
      // 尝试解析其他格式
      const date = new Date(str)
      if (isNaN(date.getTime())) return str
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
  }
}
</script>

<style lang="less" scoped>
.download-page {
  min-height: 100vh;
  background: #f5f5f5;
}

.page-header {
  position: sticky;
  top: 198px;
  z-index: 150;
  background: white;
  border-bottom: 1px solid #eee;
  padding: 15px 0;

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
  }

  .header-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 20px;
    width: 100%;
  }

  .header-left {
    display: flex;
    align-items: baseline;
    gap: 4px;
  }

  .breadcrumb {
    display: flex;
    align-items: center;
    font-size: 14px;
    color: #666;
    margin-left: 30px;

    a {
      color: #4ecdc4;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }

    span {
      margin: 0 8px;
      color: #999;
    }
  }

  .current-category-title {
    font-size: 24px;
    font-weight: bold;
    color: #4ecdc4;
    margin: 0;
  }

  .search-box {
    display: flex;
    gap: 10px;
    align-items: center;
    margin-left: auto;

    input {
      padding: 8px 15px;
      border: 1px solid #ddd;
      border-radius: 5px;
      font-size: 14px;
      width: 220px;
      outline: none;

      &:focus {
        border-color: #4ecdc4;
      }
    }

    button {
      padding: 8px 20px;
      background: #4ecdc4;
      color: white;
      border: none;
      border-radius: 5px;
      font-size: 14px;
      cursor: pointer;
      transition: opacity 0.3s;

      &:hover {
        opacity: 0.9;
      }
    }
  }
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 30px 20px;
}

.main-content {
  width: 100%;
}

.download-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.download-item {
  display: flex;
  align-items: center;
  background: white;
  border-radius: 10px;
  padding: 20px 24px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: transform 0.3s, box-shadow 0.3s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.12);
  }
}

.download-icon {
  flex-shrink: 0;
  margin-right: 18px;
}

.download-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.download-title {
  font-size: 17px;
  font-weight: 600;
  color: #333;
  margin: 0;
  line-height: 1.4;
}

.download-meta {
  display: flex;
  gap: 20px;

  .meta-item {
    font-size: 13px;
    color: #999;
  }
}

.download-action {
  flex-shrink: 0;
  margin-left: 10px;
}

.download-btn {
  padding: 10px 28px;
  background: #4ecdc4;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: #3abbb4;
  }
}

.pagination-wrapper {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 6px;
  margin-top: 30px;
  padding: 20px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);

  .total-text {
    font-size: 14px;
    color: #333;
    margin-right: 10px;
    line-height: 1.4;
  }

  .page-btn {
    width: 38px;
    height: 32px;
    background: white;
    color: #333;
    border: 1px solid #eee;
    border-radius: 8px;
    font-size: 14px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s;

    &:hover {
      border-color: #4ecdc4;
      color: #4ecdc4;
    }

    &.active {
      border-color: #4ecdc4;
      color: #4ecdc4;
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  .ellipsis {
    font-size: 14px;
    color: #999;
    width: 24px;
    text-align: center;
  }

  .page-size-select {
    width: 100px;
    height: 32px;
    background: white;
    color: #333;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 14px;
    cursor: pointer;
    padding: 0 30px 0 10px;
    outline: none;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    transition: border-color 0.3s;
    background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%234ecdc4' d='M6 8L0 2L1.4 0.6L6 5.2L10.6 0.6L12 2L6 8Z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 10px center;
    
    &:focus {
      border-color: #4ecdc4;
    }
  }

  .page-size-select option {
    background: white;
    color: #333;
    
    &:checked {
      background: #4ecdc4;
      color: white;
    }
  }
}
</style>
